import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
// Navigation removed — dashboard lives within tabbed CompliancePage

const STATUS_CONFIG = {
  current: { label: 'Current', color: '#10b981', bg: '#ecfdf5' },
  expiring_soon: { label: 'Expiring Soon', color: '#d97706', bg: '#fffbeb' },
  expired: { label: 'Expired', color: '#ef4444', bg: '#fef2f2' },
  pending: { label: 'Pending', color: '#3b82f6', bg: '#eff6ff' },
  not_applicable: { label: 'N/A', color: '#6b7280', bg: '#f3f4f6' },
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + 'T00:00:00');
  return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
}

function calcStatus(item) {
  if (item.manual_status === 'not_applicable') return 'not_applicable';
  if (item.manual_status === 'pending') return 'pending';
  if (!item.expiration_date) return 'current';
  const days = daysUntil(item.expiration_date);
  if (days === null) return 'current';
  if (days < 0) return 'expired';
  if (days <= 30) return 'expiring_soon';
  return 'current';
}

export default function ComplianceDashboardPage() {
  const { can } = useAuth();
  // const navigate removed — tabbed layout
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [allItems, notifs] = await Promise.all([
        api.getComplianceItems(),
        api.getComplianceNotifications ? api.getComplianceNotifications({ unread: true }).catch(() => []) : Promise.resolve([]),
      ]);
      setItems(Array.isArray(allItems) ? allItems : []);
      setNotifications(Array.isArray(notifs) ? notifs : []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (!can('manage_admin')) {
    return <div className="page-content"><p>You do not have permission to access this page.</p></div>;
  }

  // Compute stats
  const stats = { total: 0, current: 0, expiring_soon: 0, expired: 0, pending: 0, not_applicable: 0, missing_docs: 0, required_expired: 0 };
  const expiredItems = [];
  const expiringSoonItems = [];
  const missingDocItems = [];
  const byEntity = { employee: { total: 0, issues: 0 }, company: { total: 0, issues: 0 }, facility: { total: 0, issues: 0 }, truck: { total: 0, issues: 0 } };
  const byOwner = {};

  items.forEach(item => {
    const s = item.computed_status || calcStatus(item);
    stats.total++;
    if (stats[s] !== undefined) stats[s]++;

    if (s === 'expired') expiredItems.push(item);
    if (s === 'expiring_soon') expiringSoonItems.push(item);
    if (item.doc_count === 0 && item.is_required && s !== 'not_applicable') {
      stats.missing_docs++;
      missingDocItems.push(item);
    }
    if (s === 'expired' && item.is_required) stats.required_expired++;

    // By entity
    if (byEntity[item.entity_type]) {
      byEntity[item.entity_type].total++;
      if (s === 'expired' || s === 'expiring_soon') byEntity[item.entity_type].issues++;
    }

    // By owner
    const ownerName = item.owner_name || 'Unassigned';
    if (!byOwner[ownerName]) byOwner[ownerName] = { total: 0, expired: 0, expiring: 0 };
    byOwner[ownerName].total++;
    if (s === 'expired') byOwner[ownerName].expired++;
    if (s === 'expiring_soon') byOwner[ownerName].expiring++;
  });

  // Sort expired by most overdue first
  expiredItems.sort((a, b) => (a.expiration_date || '').localeCompare(b.expiration_date || ''));
  expiringSoonItems.sort((a, b) => (a.expiration_date || '').localeCompare(b.expiration_date || ''));

  const overallHealth = stats.expired > 0 ? 'red' : stats.expiring_soon > 0 ? 'yellow' : 'green';
  const healthConfig = {
    green: { label: 'All Clear', color: '#10b981', bg: '#ecfdf5', icon: '\u2705' },
    yellow: { label: 'Needs Attention', color: '#d97706', bg: '#fffbeb', icon: '\u26A0\uFE0F' },
    red: { label: 'Action Required', color: '#ef4444', bg: '#fef2f2', icon: '\uD83D\uDEA8' },
  };
  const health = healthConfig[overallHealth];

  if (loading) {
    return <div className="admin-page"><div className="loading-spinner">Loading dashboard...</div></div>;
  }

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>Compliance Dashboard</h1>
      </div>

      {/* Overall Health Banner */}
      <div style={{
        background: health.bg, border: `1.5px solid ${health.color}`, borderRadius: '14px',
        padding: '16px 24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '16px',
      }}>
        <span style={{ fontSize: '28px' }}>{health.icon}</span>
        <div>
          <div style={{ fontSize: '18px', fontWeight: 800, color: health.color }}>{health.label}</div>
          <div style={{ fontSize: '13px', color: '#64748b' }}>
            {stats.total} tracked items &middot; {stats.expired} expired &middot; {stats.expiring_soon} expiring soon &middot; {stats.missing_docs} missing documents
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid">
        {[
          { label: 'Total Items', value: stats.total, color: '#3b82f6' },
          { label: 'Current', value: stats.current, color: '#10b981' },
          { label: 'Expiring Soon', value: stats.expiring_soon, color: '#d97706' },
          { label: 'Expired', value: stats.expired, color: '#ef4444' },
          { label: 'Missing Docs', value: stats.missing_docs, color: '#8b5cf6' },
          { label: 'Required Expired', value: stats.required_expired, color: '#dc2626' },
        ].map(kpi => (
          <div key={kpi.label} className="kpi-card" style={{ '--kpi-accent': kpi.color }}>
            <div className="kpi-value">{kpi.value}</div>
            <div className="kpi-label">{kpi.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Expired Items */}
        <div style={{ background: 'white', borderRadius: '14px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#ef4444', marginBottom: '12px' }}>
            Expired Items ({expiredItems.length})
          </h3>
          {expiredItems.length === 0 ? (
            <p style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>None — you're all clear!</p>
          ) : (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {expiredItems.slice(0, 20).map(item => {
                const days = daysUntil(item.expiration_date);
                return (
                  <div key={item.id} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600 }}>{item.title}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                        {item.entity_type}: {item.entity_name} &middot; {item.category_name}
                      </div>
                    </div>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#ef4444', whiteSpace: 'nowrap' }}>
                      {Math.abs(days)}d overdue
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Expiring Soon */}
        <div style={{ background: 'white', borderRadius: '14px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#d97706', marginBottom: '12px' }}>
            Expiring Within 30 Days ({expiringSoonItems.length})
          </h3>
          {expiringSoonItems.length === 0 ? (
            <p style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>Nothing expiring soon.</p>
          ) : (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {expiringSoonItems.slice(0, 20).map(item => {
                const days = daysUntil(item.expiration_date);
                return (
                  <div key={item.id} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600 }}>{item.title}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                        {item.entity_type}: {item.entity_name} &middot; {item.category_name}
                      </div>
                    </div>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: days <= 7 ? '#ef4444' : '#d97706', whiteSpace: 'nowrap' }}>
                      {days}d left
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Missing Documents */}
        <div style={{ background: 'white', borderRadius: '14px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#8b5cf6', marginBottom: '12px' }}>
            Missing Required Documents ({missingDocItems.length})
          </h3>
          {missingDocItems.length === 0 ? (
            <p style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>All required items have documents.</p>
          ) : (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {missingDocItems.slice(0, 20).map(item => (
                <div key={item.id} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>{item.title}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                    {item.entity_type}: {item.entity_name} &middot; {item.category_name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* By Owner */}
        <div style={{ background: 'white', borderRadius: '14px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>
            Items by Owner
          </h3>
          {Object.keys(byOwner).length === 0 ? (
            <p style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>No items tracked yet.</p>
          ) : (
            <div>
              {Object.entries(byOwner)
                .sort((a, b) => (b[1].expired + b[1].expiring) - (a[1].expired + a[1].expiring))
                .map(([name, data]) => (
                <div key={name} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>{name}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>{data.total} item{data.total !== 1 ? 's' : ''}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {data.expired > 0 && (
                      <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: 700, background: '#fef2f2', color: '#ef4444' }}>
                        {data.expired} expired
                      </span>
                    )}
                    {data.expiring > 0 && (
                      <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: 700, background: '#fffbeb', color: '#d97706' }}>
                        {data.expiring} expiring
                      </span>
                    )}
                    {data.expired === 0 && data.expiring === 0 && (
                      <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: 700, background: '#ecfdf5', color: '#10b981' }}>
                        All clear
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Entity Summary */}
        <div style={{ gridColumn: '1 / -1', background: 'white', borderRadius: '14px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '16px' }}>
            Compliance by Entity Type
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
            {[
              { type: 'employee', label: 'Employees', icon: '\uD83D\uDC64' },
              { type: 'company', label: 'Company', icon: '\uD83C\uDFE2' },
              { type: 'facility', label: 'Facilities', icon: '\uD83C\uDFED' },
              { type: 'truck', label: 'Trucks', icon: '\uD83D\uDE9A' },
            ].map(({ type, label, icon }) => {
              const data = byEntity[type];
              const pct = data.total > 0 ? Math.round(((data.total - data.issues) / data.total) * 100) : 100;
              const barColor = pct === 100 ? '#10b981' : pct >= 80 ? '#d97706' : '#ef4444';
              return (
                <div key={type} style={{ textAlign: 'center', padding: '16px', borderRadius: '12px', border: '1px solid #edf2f7' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>{icon}</div>
                  <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '4px' }}>{label}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px' }}>{data.total} items &middot; {data.issues} issues</div>
                  <div style={{ background: '#f1f5f9', borderRadius: '999px', height: '6px', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: '999px', transition: 'width 0.5s ease' }} />
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: barColor, marginTop: '4px' }}>{pct}% compliant</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
